// --------------------------------------------------------------
// Created On: 2025-08-20
// Author: Hannah Vaughan
//
// Last Modified: 2026-04-13
// Modified By: Dimitra Weinstein
//
// Copyright 2025 - 2026 © Cornell Pump Company, All Rights Reserved
// --------------------------------------------------------------

import React, { Fragment, useEffect, useState } from "react";
import InfoBanner from "../InfoBanner/InfoBanner";
import formatDateShortLocal from "../../utilities/time/formatDateShortLocal";
import isoToTimePassed from "../../utilities/time/isoToTimePassed";
import userHasPermission from "../../utilities/userHasPermission";
import { APPLY_FIRMWARE_PERMISSION, MIGRATE_ASSETS_PERMISSION } from "../../constants/permissions";
import formatDateLocal from "../../utilities/time/formatDateLocal";
import apiRequest from "../../utilities/api/apiRequest";
import { API, GATEWAY_TIMEOUT_STATUS } from "../../constants/miscellaneous";
import useReduxSelector from "../../hooks/useReduxSelector";
import ErrorBanner from "../ErrorBanner/ErrorBanner";
import ConfirmFirmwareUpdateModal from "../ConfirmFirmwareUpdateModal/ConfirmFirmwareUpdateModal";
import ConfirmMigrationModal from "../ConfirmMigrationModal/ConfirmMigrationModal";
import SuccessBanner from "../SuccessBanner/SuccessBanner";
import DotLoader from "../DotLoader/DotLoader";

// Container for all status banners relating to an asset, including configuration retries, asset migrations,
// and firmware updates.
export default function AssetStatusBannerContainer(props: Props): Component {
  const [configurationErrorMessage, setConfigurationErrorMessage] = useState<string>("");
  const [firmwareErrorMessage, setFirmwareErrorMessage] = useState<string>("");
  const [showMigrateAsset, setShowMigrateAsset] = useState<boolean>(false);
  const [showUpdateFirmwareModal, setShowUpdateFirmwareModal] = useState<boolean>(false);
  const currentUser = useReduxSelector((state) => state.currentUser);
  const userCompanyId = currentUser.isInSubsidiary ? currentUser.subsidiaryCompanyId : currentUser.companyId;
  const now = new Date().toISOString();

  // Applies a configuration error message if the configuration was being applied (i.e. was in progress) and it fails.
  useEffect(() => {
    if (props.previousConfigurationStatus === "IN_PROGRESS") {
      if (props.configurationStatus === "FAILED") {
        setConfigurationErrorMessage("Asset configuration has failed.");
      } else {
        setConfigurationErrorMessage("");
      }
    }
  }, [props.configurationStatus, props.previousConfigurationStatus]);

  // Applies a firmware error message if the firmware was being updated (i.e. was in progress) and it fails.
  useEffect(() => {
    if (props.previousFirmwareStatus === "IN_PROGRESS") {
      if (props.firmwareStatus === "FAILED") {
        setFirmwareErrorMessage(
          `Firmware update for monitoring device ${props.deviceType} '${props.deviceIdentifier}' failed.`
        );
      } else {
        setFirmwareErrorMessage("");
      }
    }
  }, [props.firmwareStatus, props.previousFirmwareStatus]);

  // Attempts to reconfigure the monitoring device of the current asset.
  async function retryConfiguration(): Promise<void> {
    props.onChangeLoading(true);
    const [response] = (await apiRequest(
      `${API}/company/${userCompanyId}/asset/${props.assetId}/configretry`,
      "POST",
      null
    )) as [Response, MessageResponseBody];
    props.onChangeLoading(false);
    if (response.ok) {
      setConfigurationErrorMessage("");
      props.onApplyConfiguration("IN_PROGRESS");
    } else if (response.status === GATEWAY_TIMEOUT_STATUS) {
      setConfigurationErrorMessage("Request timeout. Unable to reapply configuration.");
      props.onApplyConfiguration("FAILED");
    } else {
      setConfigurationErrorMessage("Unable to configure asset.");
      props.onApplyConfiguration("FAILED");
    }
  }

  // Sets the configuration status to "IN_PROGRESS" when starting the migration is successful.
  function handleMigrateAsset(): void {
    props.onApplyConfiguration("IN_PROGRESS");
    setShowMigrateAsset(false);

    // Due to state, the current configuration status will become the previous configuration status in this function.
    props.onMigrateAsset("IN_PROGRESS", props.configurationStatus);
  }

  // Cleans up state when the migrate asset modal is closed without migrating the asset.
  function handleCloseMigrationModal(): void {
    setConfigurationErrorMessage("");
    props.onChangeLoading(false);
    setShowMigrateAsset(false);
  }

  // Sets the firmware status to "IN_PROGRESS" when starting a firmware update is successful.
  function handleUpdateFirmware(): void {
    props.onUpdateFirmware("IN_PROGRESS");
    setShowUpdateFirmwareModal(false);
  }

  // Cleans up state when the update firmware modal is closed without updating the firmware.
  function handleCloseFirmwareUpdateModal(): void {
    setFirmwareErrorMessage("");
    props.onChangeLoading(false);
    setShowUpdateFirmwareModal(false);
  }

  return (
    <Fragment>
      {props.lastComm !== null && props.hideLastComm !== true && (
        <InfoBanner
          message={`This asset reported new data ${isoToTimePassed(props.lastComm)} ago (${formatDateShortLocal(
            props.lastComm
          )}).`}
        />
      )}

      {!props.loading && props.isMigrating && props.configurationStatus !== "IN_PROGRESS" && (
        <Fragment>
          <ErrorBanner
            message={
              configurationErrorMessage.length > 0
                ? configurationErrorMessage
                : "This asset has not completed migration. " +
                  "There are some limitations on configuration, alerting, and remote operation until " +
                  "this migration is complete."
            }
          >
            {userHasPermission([[MIGRATE_ASSETS_PERMISSION]]) && !props.isRented && (
              <button className="btn btn-sm btn-primary ms-2" type="button" onClick={() => setShowMigrateAsset(true)}>
                {props.configurationStatus === "FAILED" && props.previousConfigurationStatus === "IN_PROGRESS"
                  ? "Retry Configuration"
                  : "Migrate Asset"}
              </button>
            )}
          </ErrorBanner>
        </Fragment>
      )}

      {!props.loading && !props.isMigrating && props.configurationStatus === "FAILED" && (
        <Fragment>
          <ErrorBanner
            message={
              configurationErrorMessage.length > 0
                ? configurationErrorMessage
                : `This asset failed to update its monitoring device configuration settings${
                    props.lastConfigAttemptUtc !== undefined && props.lastConfigAttemptUtc !== null
                      ? ` on ${formatDateLocal(props.lastConfigAttemptUtc)}`
                      : ""
                  }. ` +
                  "This could possibly be due to not being able to establish a stable connection with " +
                  "the physical device. You can attempt to reapply the configuration settings. " +
                  "You will get a notification when the attempt is complete."
            }
          >
            <button
              className="btn btn-sm btn-primary ms-2"
              type="button"
              onClick={() => retryConfiguration()}
              disabled={props.loading}
            >
              Retry Configuration
            </button>
          </ErrorBanner>
        </Fragment>
      )}

      {!props.loading &&
        props.firmwareStatus !== "IN_PROGRESS" &&
        props.onLatestFirmware === false &&
        userHasPermission([[APPLY_FIRMWARE_PERMISSION]]) && (
          <Fragment>
            <ErrorBanner
              message={
                firmwareErrorMessage.length > 0
                  ? firmwareErrorMessage
                  : "This asset is not currently using the latest firmware that is supported by its monitoring device."
              }
            >
              <button
                className="btn btn-sm btn-primary ms-2"
                type="button"
                onClick={() => setShowUpdateFirmwareModal(true)}
              >
                Update Firmware
              </button>
            </ErrorBanner>
          </Fragment>
        )}

      {/* Info banners informing the user that either a configuration or
        a firmware update is actively being applied. */}
      {props.firmwareStatus === "IN_PROGRESS" && userHasPermission([[APPLY_FIRMWARE_PERMISSION]]) && (
        <InfoBanner
          message={
            props.lastUpdateFirmwareAttemptUtc !== null
              ? `Firmware update for monitoring device ${props.deviceType} '${props.deviceIdentifier}' is in progress. ` +
                `Started applying firmware at ${formatDateLocal(props.lastUpdateFirmwareAttemptUtc ?? now)}.`
              : `Firmware update for monitoring device ${props.deviceType} '${props.deviceIdentifier}' is in progress.`
          }
        >
          <DotLoader loading isLarge className="" />
        </InfoBanner>
      )}

      {props.configurationStatus === "IN_PROGRESS" && (
        <InfoBanner
          message={
            props.lastConfigAttemptUtc !== null
              ? "Configuration for this asset is in progress. " +
                `Started applying configuration at ${formatDateLocal(props.lastConfigAttemptUtc)}.`
              : "Configuration for this asset is in progress."
          }
        >
          <DotLoader loading isLarge className="" />
        </InfoBanner>
      )}

      {/* Success banners informing the user that either a configuration or a
        firmware update was successfully applied while the user is on the page. */}
      {props.firmwareStatus === "SUCCEEDED" &&
        props.previousFirmwareStatus !== "SUCCEEDED" &&
        props.onLatestFirmware === true &&
        userHasPermission([[APPLY_FIRMWARE_PERMISSION]]) && (
          <SuccessBanner
            message={
              `Firmware update for monitoring device ${props.deviceType} '${props.deviceIdentifier}' ` +
              "completed successfully."
            }
          />
        )}

      {props.configurationStatus === "SUCCEEDED" && props.previousConfigurationStatus !== "SUCCEEDED" && (
        <SuccessBanner message="Asset configuration has completed successfully." />
      )}

      <ConfirmMigrationModal
        showModal={showMigrateAsset}
        assetId={props.assetId}
        onConfirm={() => handleMigrateAsset()}
        onCancel={() => handleCloseMigrationModal()}
      />

      <ConfirmFirmwareUpdateModal
        showModal={showUpdateFirmwareModal}
        deviceId={props.deviceId}
        onClose={() => handleCloseFirmwareUpdateModal()}
        onUpdate={() => handleUpdateFirmware()}
        onCancel={() => handleCloseFirmwareUpdateModal()}
      />
    </Fragment>
  );
}

interface Props {
  loading: boolean;
  assetId: string;
  deviceId: number | null;
  firmwareStatus: Status;
  configurationStatus: Status;
  previousFirmwareStatus: Status;
  previousConfigurationStatus: Status;
  deviceIdentifier: string;
  deviceType: string;
  isMigrating: boolean;
  isRented: boolean;
  onLatestFirmware: boolean | null;
  lastConfigAttemptUtc: string | null;
  lastUpdateFirmwareAttemptUtc: string | null;
  lastComm: string | null;
  hideLastComm?: boolean;
  onChangeLoading: (loading: boolean) => void;
  onMigrateAsset: (status: Status, previousStatus: Status) => void;
  onApplyConfiguration: (status: Status) => void;
  onUpdateFirmware: (status: Status) => void;
}

interface MessageResponseBody {
  message: string;
}

type Status = "IN_PROGRESS" | "SUCCEEDED" | "FAILED";
